import { LinearGradient } from "expo-linear-gradient";

import Icon from "@expo/vector-icons/MaterialCommunityIcons";
/* ----------------- UI --------------- */
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import AvatarGenerator from "../AvatarGenerator/AvatarGenerator";
import Box from "../Box";
import CustomAvatar from "../CustomAvatar";
import CustomChip from "../CustomChip";

/* ----------------- Constants --------------- */
import { rankStyles } from "./constants";

type Props = {
	rank: number;
	xp: string;
	title: string;
	image: string;
	avatarType?: "initials" | "identicon";
	isCurrentUser?: boolean;
};

type UserTitleProps = {
	title: string;
	isCurrentUser: boolean;
};

const UserTitle = ({ title, isCurrentUser }: UserTitleProps) => {
	const theme = useAppTheme();
	return (
		<Box
			style={{
				borderColor: isCurrentUser ? theme.colors.primary : "transparent",
				width: "100%",
				borderRadius: 999,
				paddingVertical: 2,
				borderWidth: 1,
				backgroundColor: isCurrentUser
					? theme.colors.primaryContainer
					: "transparent",
			}}
		>
			<Text
				variant="titleMedium"
				numberOfLines={1}
				style={{
					textAlign: "center",
					color: isCurrentUser
						? theme.colors.onPrimaryContainer
						: theme.colors.onSurface,
				}}
			>
				{title}
			</Text>
		</Box>
	);
};

type AvatarSectionProps = {
	image: string;
	title: string;
	avatarType?: "initials" | "identicon";
	isCurrentUser: boolean;
	borderGradientColors: readonly [string, string];
};

const AvatarSection = ({
	image,
	title,
	avatarType,
	isCurrentUser,
}: AvatarSectionProps) => {
	const avatarContent =
		image && !avatarType ? (
			<CustomAvatar size={48} avatar={image} />
		) : (
			<AvatarGenerator
				firstName={title}
				lastName={title}
				size={48}
				avatarType={avatarType}
				customStyle={{ position: "static" }}
			/>
		);

	return (
		<Box alignItems="center" gap={8} paddingBottom={8} paddingHorizontal={4}>
			<Box style={{ borderRadius: 999 }}>{avatarContent}</Box>
			{title && <UserTitle title={title} isCurrentUser={isCurrentUser} />}
		</Box>
	);
};

type PodiumTopProps = {
	rank: number;
	marginTop: number;
	topSurfaceColor: string;
};

const PodiumTop = ({ rank, marginTop, topSurfaceColor }: PodiumTopProps) => (
	<Box
		width="100%"
		height={24}
		style={{
			backgroundColor: topSurfaceColor,
			marginTop,
			left: rank === 2 ? 4 : rank === 3 ? -4 : 0,
			transform:
				rank === 2
					? [{ skewX: "-20deg" }]
					: rank === 3
						? [{ skewX: "20deg" }]
						: [],
		}}
	/>
);

type PodiumContentProps = {
	rank: number;
	xp: string;
	textColor: string;
	chipColor: string;
};

const PodiumContent = ({
	rank,
	xp,
	textColor,
	chipColor,
}: PodiumContentProps) => (
	<Box
		width="100%"
		height="100%"
		alignItems="center"
		justifyContent="center"
		gap={8}
	>
		<Text
			variant={rank === 1 ? "displayLarge" : "displayMedium"}
			style={{ color: textColor }}
		>
			{rank}
		</Text>
		<CustomChip
			textStyle={{ color: textColor }}
			textVariant="labelMedium"
			style={{ backgroundColor: chipColor }}
		>
			{`${xp.toLocaleString()} XP`}
		</CustomChip>
	</Box>
);

const LeaderboardPodium = (props: Props) => {
	const { rank, image, title, xp, avatarType, isCurrentUser = false } = props;
	const {
		textColor,
		height,
		marginTop,
		gradientColors,
		borderGradientColors,
		chipColor,
		topSurfaceColor,
	} = rankStyles[rank - 1];
	return (
		<Box
			flex={1}
			display="flex"
			flexDirection="column"
			justifyContent="flex-end"
			position="relative"
			overflow="hidden"
		>
			{rank === 1 && (
				<Box alignSelf="center" paddingBottom={8} paddingTop={16}>
					<Icon name="trophy-variant-outline" size={24} color={textColor} />
				</Box>
			)}
			<AvatarSection
				image={image}
				title={title}
				avatarType={avatarType}
				isCurrentUser={isCurrentUser}
				borderGradientColors={borderGradientColors as readonly [string, string]}
			/>
			<PodiumTop
				rank={rank}
				marginTop={marginTop}
				topSurfaceColor={topSurfaceColor}
			/>
			<LinearGradient
				colors={gradientColors as readonly [string, string]}
				start={{ x: 0, y: 0 }}
				end={{ x: 0, y: 1 }}
				style={{
					zIndex: 100,
					width: "100%",
					height,
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<PodiumContent
					rank={rank}
					xp={xp}
					textColor={textColor}
					chipColor={chipColor}
				/>
			</LinearGradient>
		</Box>
	);
};

export default LeaderboardPodium;
